/** @jsx jsx */
import { Fragment, FC, useState } from 'react'
import { jsx } from '@emotion/core'
import { Formik } from 'formik'
import * as Yup from 'yup'
import axios from 'axios'
import { navigate } from 'gatsby'

import {
	LayoutVerticalCadence,
	LAYOUT_V_CADENCE_SIZE_SMALL,
} from '../../../components/content/Layout'
import Button from '../../../components/Button'

import TextInput from '../../../components/Form/TextInput'
import FormField from '../../../components/Form/FormField'
import {
	INPUT_VALIDATION_STATE_DEFAULT,
	INPUT_VALIDATION_STATE_INVALID,
} from '../../../components/Form/constants'
import ErrorInline from '../../../components/content/ErrorInline'
import { trackEvent } from '../../../tracking'

type Props = {}

type RegistrationInfo = {
	email: string
	name: string
}

const RegistrationSchema = Yup.object().shape({
	name: Yup.string().required('Required'),
	email: Yup.string()
		.email('Invalid email')
		.required('Required'),
})

const initialRegValues: RegistrationInfo = {
	email: '',
	name: '',
}

const EthicsRegisterForm: FC<Props> = () => {
	const [serverErrors, setServerErrors] = useState<string[] | null>(null)

	return (
		<Fragment>
			<Formik
				initialValues={initialRegValues}
				validationSchema={RegistrationSchema}
				onSubmit={(values, { setSubmitting }) => {
					setServerErrors(null)

					axios
						.post('/.netlify/functions/media-registration', {
							email: values.email,
							name: values.name,
							media: 'design-ethics-masterclass',
						})
						.then(res => {
							// Navigate to watch page
							setSubmitting(false)

							trackEvent({
								action: 'register',
								event_category: 'Media',
								event_label: 'Design Ethics',
								value: null,
							})

							navigate(`/academy/design-ethics/watch?k=${res.data.key}`)
						})
						.catch(error => {
							if (error.response) {
								try {
									const errors = Object.keys(error.response.data.errors).reduce(
										(all: string[], key: string) => {
											return [...all, error.response.data.errors[key].join('')]
										},
										[]
									)

									setServerErrors(errors)
								} catch (e) {
									console.error(e)
									setServerErrors(['A server error occurred.'])
								}
							} else if (error.request) {
								console.error(error.request)
								setServerErrors(['A network error occurred.'])
							} else {
								console.error('Error', error.message)
								setServerErrors(['An unknown error occurred.'])
							}

							setSubmitting(false)
						})
				}}
			>
				{({
					values,
					errors,
					touched,
					handleChange,
					handleBlur,
					handleSubmit,
					isSubmitting,
				}) => (
					<form onSubmit={handleSubmit}>
						<LayoutVerticalCadence size={LAYOUT_V_CADENCE_SIZE_SMALL}>
							{!isSubmitting && serverErrors && (
								<ErrorInline>
									{serverErrors.map((e, idx) => {
										return <div key={idx}>{e}</div>
									})}
								</ErrorInline>
							)}
							<FormField
								label="Name"
								inputID="name"
								validationState={
									errors.name && touched.name
										? INPUT_VALIDATION_STATE_INVALID
										: INPUT_VALIDATION_STATE_DEFAULT
								}
								invalidMessage={errors.name}
							>
								<TextInput
									id="name"
									type="text"
									name="name"
									onChange={handleChange}
									onBlur={handleBlur}
									value={values.name}
									validationState={
										errors.name && touched.name
											? INPUT_VALIDATION_STATE_INVALID
											: INPUT_VALIDATION_STATE_DEFAULT
									}
									disabled={isSubmitting}
								/>
							</FormField>
							<FormField
								label="Email"
								inputID="email"
								validationState={
									errors.email && touched.email
										? INPUT_VALIDATION_STATE_INVALID
										: INPUT_VALIDATION_STATE_DEFAULT
								}
								invalidMessage={errors.email}
							>
								<TextInput
									id="email"
									type="email"
									name="email"
									onChange={handleChange}
									onBlur={handleBlur}
									value={values.email}
									validationState={
										errors.email && touched.email
											? INPUT_VALIDATION_STATE_INVALID
											: INPUT_VALIDATION_STATE_DEFAULT
									}
									disabled={isSubmitting}
								/>
							</FormField>
							<div
								css={{
									paddingTop: '1rem',
								}}
							>
								<Button layout="block" type="submit" disabled={isSubmitting}>
									Watch Now
								</Button>
							</div>
						</LayoutVerticalCadence>
					</form>
				)}
			</Formik>
		</Fragment>
	)
}

export default EthicsRegisterForm
